// pages/FAQ/index.jsx
import React, { useEffect, useState } from "react";
import { Container } from "../../styles/components";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import useNavbar from "../../hooks/useNavbar";
import {
	FAQWrapper,
	FAQHeader,
	SearchContainer,
	SearchInput,
	FAQContent,
	CategorySection,
	QuestionAccordion,
	AccordionHeader,
	AccordionContent,
} from "./styles";

const faqData = {
	"Subscription & Pricing": [
		{
			question: "What subscription plans do you offer?",
			answer:
				"We offer three main subscription plans: Basic (quarterly), Premium (bi-monthly), and VIP (monthly). Each plan includes different numbers of session credits and features tailored to your needs.",
		},
		{
			question: "Can I change my subscription plan?",
			answer:
				"Yes, you can change your subscription plan at the end of your current billing cycle. For the Basic plan, that's every 3 months, Premium every 2 months, and VIP monthly.",
		},
		{
			question: "What happens to unused credits?",
			answer:
				"Credits roll over to the next period up to a maximum limit specified in your plan. Additional purchased credits have different expiration terms.",
		},
	],
	"Photography Sessions": [
		{
			question: "How long is each photography session?",
			answer:
				"Standard sessions are typically 1-2 hours long, depending on your package and requirements. Newborn sessions might take longer to ensure we capture the perfect moments.",
		},
		{
			question: "What should we wear to the session?",
			answer:
				"We recommend wearing solid colors and coordinating outfits among family members. We'll provide detailed guidelines before your session.",
		},
		{
			question: "Can we reschedule a session?",
			answer:
				"Yes, sessions can be rescheduled with at least 48 hours notice without penalty. Last-minute cancellations may result in credit deduction.",
		},
	],
	"Photo Delivery": [
		{
			question: "How long does it take to receive our photos?",
			answer:
				"Digital photos are typically delivered within 7-14 days after your session, depending on your subscription plan. Rush delivery is available for an additional fee.",
		},
		{
			question: "How do we receive our photos?",
			answer:
				"Photos are delivered through our secure online gallery where you can download high-resolution images and share them with family and friends.",
		},
		{
			question: "Can we request specific editing styles?",
			answer:
				"Yes, we're happy to accommodate specific editing requests. Please discuss your preferences during the session booking.",
		},
	],
	"Technical & Account": [
		{
			question: "How do I book a session?",
			answer:
				"You can book sessions through your dashboard after subscribing. Select your preferred date and time from available slots.",
		},
		{
			question: "What payment methods do you accept?",
			answer:
				"We accept bank transfers for subscription payments. All payments must be completed before session confirmation.",
		},
		{
			question: "How can I update my account information?",
			answer:
				"You can update your account information through your dashboard settings. For subscription changes, please contact support.",
		},
	],
};

const FAQ = () => {
	const { setIsTransparent } = useNavbar();
	const [searchQuery, setSearchQuery] = useState("");
	const [expandedQuestions, setExpandedQuestions] = useState({});
	const [filteredFAQ, setFilteredFAQ] = useState(faqData);

	useEffect(() => {
		setIsTransparent(false);
		return () => setIsTransparent(true);
	}, [setIsTransparent]);

	useEffect(() => {
		if (searchQuery.trim() === "") {
			setFilteredFAQ(faqData);
			return;
		}

		const filtered = {};
		Object.entries(faqData).forEach(([category, questions]) => {
			const filteredQuestions = questions.filter(
				(q) =>
					q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
					q.answer.toLowerCase().includes(searchQuery.toLowerCase())
			);
			if (filteredQuestions.length > 0) {
				filtered[category] = filteredQuestions;
			}
		});
		setFilteredFAQ(filtered);
	}, [searchQuery]);

	const toggleQuestion = (categoryIndex, questionIndex) => {
		const key = `${categoryIndex}-${questionIndex}`;
		setExpandedQuestions((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	return (
		<FAQWrapper>
			<FAQHeader>
				<Container>
					<h1>Frequently Asked Questions</h1>
					<p>Find answers to common questions about our services</p>

					<SearchContainer>
						<Search size={20} />
						<SearchInput
							type="text"
							placeholder="Search questions..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
					</SearchContainer>
				</Container>
			</FAQHeader>

			<Container>
				<FAQContent>
					{Object.entries(filteredFAQ).map(
						([category, questions], categoryIndex) => (
							<CategorySection key={category}>
								<h2>{category}</h2>
								{questions.map((item, questionIndex) => {
									const key = `${categoryIndex}-${questionIndex}`;
									const isExpanded = expandedQuestions[key];

									return (
										<QuestionAccordion key={questionIndex}>
											<AccordionHeader
												onClick={() =>
													toggleQuestion(categoryIndex, questionIndex)
												}
												isExpanded={isExpanded}
											>
												<h3>{item.question}</h3>
												{isExpanded ? (
													<ChevronUp size={20} />
												) : (
													<ChevronDown size={20} />
												)}
											</AccordionHeader>
											<AccordionContent isExpanded={isExpanded}>
												<p>{item.answer}</p>
											</AccordionContent>
										</QuestionAccordion>
									);
								})}
							</CategorySection>
						)
					)}

					{Object.keys(filteredFAQ).length === 0 && (
						<div className="no-results">
							<p>No questions found matching your search.</p>
						</div>
					)}
				</FAQContent>
			</Container>
		</FAQWrapper>
	);
};

export default FAQ;
